import React from 'react';
import {
  Box,
  Typography,
  Slider,
  Switch,
  FormControlLabel,
} from '@mui/material';
import Iconify from 'src/components/iconify';
// import TuneIcon from '@mui/icons-material/Tune';
// import BubbleChartIcon from '@mui/icons-material/BubbleChart';
// import TimelineIcon from '@mui/icons-material/Timeline';
// import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';

const GraphControls = ({ config, onConfigChange }) => {
  const handleNodeSizeChange = (event, value) => {
    onConfigChange({ ...config, nodeSize: value });
  };

  const handleLinkWidthChange = (event, value) => {
    onConfigChange({ ...config, linkWidth: value });
  };

  const handleParticlesToggle = (event) => {
    onConfigChange({ ...config, particlesEnabled: event.target.checked });
  };

  return (
    <Box
      sx={{
        width: 280,
        padding: '20px',
        borderRadius: '20px',
        background: 'rgba(20, 10, 50, 0.6)',
        backdropFilter: 'blur(20px)',
        border: '1px solid rgba(255, 255, 255, 0.08)',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
      }}
    >
      {/* Header */}
      <Box 
        display="flex" 
        alignItems="center" 
        mb={3}
        sx={{
          pb: 2,
          borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
        }}
      >
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: '10px',
            background: 'linear-gradient(135deg, #00f5ff, #b24cff)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mr: 1.5,
            boxShadow: '0 0 20px rgba(0, 245, 255, 0.3)',
          }}
        >
          {/* <TuneIcon sx={{ fontSize: 20, color: 'white' }} /> */}
          <Iconify icon="material-symbols:tune" width={20} color="white" />

        </Box>
        <Box>
          <Typography 
            variant="subtitle1" 
            sx={{ 
              fontWeight: 600,
              fontSize: '1rem',
              color: 'white',
            }}
          >
            Graph Controls
          </Typography>
          <Typography 
            variant="caption" 
            sx={{ 
              color: 'rgba(255, 255, 255, 0.5)',
              fontSize: '0.7rem',
              textTransform: 'uppercase',
              letterSpacing: '1px',
            }}
          >
            Customize view
          </Typography>
        </Box>
      </Box>

      {/* Node Size Control */}
      <Box mb={3}>
        <Box display="flex" alignItems="center" mb={1}>
          {/* <BubbleChartIcon sx={{ fontSize: 16, color: '#b24cff', mr: 1 }} /> */}
          <Iconify icon="material-symbols:bubble-chart" width={16} color="#b24cff" />

          <Typography 
            sx={{ 
              fontSize: '0.85rem', 
              fontWeight: 500,
              color: 'rgba(255, 255, 255, 0.8)',
            }}
          >
            Node Size
          </Typography>
          <Typography 
            sx={{ 
              ml: 'auto', 
              fontSize: '0.8rem',
              color: '#00f5ff',
              fontFamily: "'JetBrains Mono', monospace",
            }}
          >
            {config.nodeSize}
          </Typography>
        </Box>
        <Slider
          value={config.nodeSize}
          onChange={handleNodeSizeChange}
          min={4}
          max={16}
          sx={{
            color: '#b24cff',
            '& .MuiSlider-track': {
              background: 'linear-gradient(90deg, #b24cff, #ff3cac)',
              border: 'none',
            },
            '& .MuiSlider-rail': {
              background: 'rgba(255, 255, 255, 0.1)',
            },
            '& .MuiSlider-thumb': {
              width: 16,
              height: 16,
              background: 'white',
              boxShadow: '0 0 10px rgba(178, 76, 255, 0.5)',
              '&:hover, &.Mui-focusVisible': {
                boxShadow: '0 0 20px rgba(178, 76, 255, 0.8)',
              },
            },
          }}
        />
      </Box>

      {/* Link Width Control */}
      <Box mb={3}>
        <Box display="flex" alignItems="center" mb={1}>
          {/* <TimelineIcon sx={{ fontSize: 16, color: '#00ff9d', mr: 1 }} /> */}
          <Typography 
            sx={{ 
              fontSize: '0.85rem', 
              fontWeight: 500,
              color: 'rgba(255, 255, 255, 0.8)',
            }}
          >
            Link Width
          </Typography>
          <Typography 
            sx={{ 
              ml: 'auto', 
              fontSize: '0.8rem',
              color: '#00f5ff',
              fontFamily: "'JetBrains Mono', monospace",
            }}
          >
            {config.linkWidth}
          </Typography>
        </Box>
        <Slider
          value={config.linkWidth}
          onChange={handleLinkWidthChange}
          min={1}
          max={5}
          sx={{
            color: '#00ff9d',
            '& .MuiSlider-track': {
              background: 'linear-gradient(90deg, #00ff9d, #00f5ff)',
              border: 'none',
            },
            '& .MuiSlider-rail': {
              background: 'rgba(255, 255, 255, 0.1)',
            },
            '& .MuiSlider-thumb': {
              width: 16,
              height: 16,
              background: 'white',
              boxShadow: '0 0 10px rgba(0, 255, 157, 0.5)',
              '&:hover, &.Mui-focusVisible': {
                boxShadow: '0 0 20px rgba(0, 255, 157, 0.8)',
              },
            },
          }}
        />
      </Box>

      {/* Particles Toggle */}
      <Box
        sx={{
          p: 1.5,
          borderRadius: '12px',
          background: config.particlesEnabled 
            ? 'rgba(0, 245, 255, 0.1)' 
            : 'rgba(255, 255, 255, 0.03)',
          border: `1px solid ${config.particlesEnabled 
            ? 'rgba(0, 245, 255, 0.2)' 
            : 'rgba(255, 255, 255, 0.05)'}`,
          transition: 'all 0.3s ease',
        }}
      >
        <FormControlLabel
          control={
            <Switch
              checked={config.particlesEnabled}
              onChange={handleParticlesToggle}
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': {
                  color: '#00f5ff',
                  '& + .MuiSwitch-track': {
                    backgroundColor: '#00f5ff',
                    opacity: 0.5,
                  },
                },
                '& .MuiSwitch-switchBase': {
                  color: 'rgba(255, 255, 255, 0.5)',
                },
                '& .MuiSwitch-track': {
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                },
              }}
            />
          }
          label={
            <Box display="flex" alignItems="center">
              {/* <AutoAwesomeIcon 
                sx={{ 
                  fontSize: 16, 
                  color: config.particlesEnabled ? '#00f5ff' : 'rgba(255, 255, 255, 0.4)',
                  mr: 1,
                  transition: 'color 0.3s ease',
                }} 
              /> */}
              <Iconify
                icon="material-symbols:auto-awesome"
                width={16}
                style={{
                  color: config.particlesEnabled
                    ? '#00f5ff'
                    : 'rgba(255, 255, 255, 0.4)',
                  marginRight: 8,
                  transition: 'color 0.3s ease',
                }}
              />
              <Typography 
                sx={{ 
                  fontSize: '0.85rem', 
                  fontWeight: 500,
                  color: config.particlesEnabled 
                    ? 'white' 
                    : 'rgba(255, 255, 255, 0.6)',
                  transition: 'color 0.3s ease',
                }}
              >
                Flow Particles
              </Typography>
            </Box>
          }
          sx={{ 
            m: 0,
            width: '100%',
            justifyContent: 'space-between',
          }}
          labelPlacement="start"
        />
      </Box>
    </Box>
  );
};

export default GraphControls;
